import { Component, OnInit, AfterViewInit, effect, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BackendService } from '@shared/backend.service';
import { ChartConfiguration } from 'chart.js';
import { ProjectDetails } from '@shared/api_interfaces';
import { ProjectService } from '@app/shell/header/header.project.service';
import { CAPTURE_NAV_CONFIG, FLAIRR_NAV_CONFIG } from '@shared/analysis-nav/nav-configs';
import { NavConfig } from '@shared/analysis-nav/analysis-nav.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-gene-analytics',
  templateUrl: './gene-analytics.component.html',
  styleUrls: ['./gene-analytics.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GeneAnalyticsComponent implements OnInit, AfterViewInit, OnDestroy {
  public _analysis_type: string = '';
  private routeSubscription: Subscription = new Subscription();
  private projectSubscription: Subscription = new Subscription();

  // Navigation configuration
  get navConfig(): NavConfig {
    return this._analysis_type === 'FLAIRR-Seq' ? FLAIRR_NAV_CONFIG : CAPTURE_NAV_CONFIG;
  }

  get analysisType(): 'capture' | 'flairr' {
    return this._analysis_type === 'FLAIRR-Seq' ? 'flairr' : 'capture';
  }
  isLoading = false;
  public projectGeneData: any = null;
  selectedLocus: string = '';
  selectedGene: string = '';
  public projectLoci: string[] = [];
  public projectGenes: string[] = [];
  public barChartPlugins = [];
  public geneOccurrencePlot: any = null;
  public alleleOccurrencePlot: any = null;
  public alleleZygosityPlot: any = null;

  public barChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    scales: {
      x: {
        ticks: { autoSkip: false },
      },
    },
  };

  constructor(
    private backendService: BackendService,
    public projectService: ProjectService,
    private route: ActivatedRoute,
  ) {
    // Set up effect to react to project changes
    effect(() => {
      const project = this.projectService.selectedProjectDetails();
      if (project && this._analysis_type) {
        this.setProject();
      }
    });
  }

  ngOnInit(): void {
    // Get analysis_type from route parameters
    this.routeSubscription = this.route.params.subscribe((params) => {
      const analysisType = params['analysis_type'];
      if (analysisType && analysisType !== this._analysis_type) {
        console.log('Route analysis_type changed to:', analysisType);
        this._analysis_type = analysisType;
        this.setProject();
      }
    });
  }

  ngAfterViewInit(): void {
    console.log('gene-analytics ngAfterViewInit');
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
    this.projectSubscription.unsubscribe();
  }

  setProject() {
    // Prevent multiple simultaneous calls
    if (this.isLoading) {
      return;
    }

    // Reset state
    this.projectGeneData = null;
    this.projectLoci = [];
    this.projectGenes = [];
    this.selectedGene = '';
    this.geneOccurrencePlot = null;
    this.alleleOccurrencePlot = null;
    this.alleleZygosityPlot = null;

    console.log('Loading project gene data for analysis_type:', this._analysis_type);

    const projectDetails = this.projectService.selectedProjectDetails();
    if (!projectDetails || !this._analysis_type) {
      console.log('Missing project details or analysis type');
      return;
    }

    this.isLoading = true;

    // Unsubscribe from previous request if any
    this.projectSubscription.unsubscribe();

    this.projectSubscription = this.backendService
      .getProjectGeneData(projectDetails.project_id, this._analysis_type)
      .subscribe({
        next: (response: any) => {
          if (response.status === 200) {
            this.projectGeneData = response;
            this.projectLoci = this.projectGeneData.loci;

            if (this.projectLoci.length > 0) {
              this.selectLocus(this.projectLoci[0]);
            }
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading project gene data:', error);
          this.isLoading = false;
        },
      });
  }

  onSelectedLocus(event: any) {
    this.selectLocus(event.target.value);
  }

  selectLocus(locus: string) {
    this.selectedLocus = locus;
    this.projectGenes = Object.keys(this.projectGeneData.allele_occurrence_plots[this.selectedLocus]);

    if (this.projectGenes.length > 0) {
      this.selectGene(this.projectGenes[0]);
    } else {
    }
  }

  onSelectedGene(event: any) {
    this.selectGene(event.target.value);
  }

  selectGene(gene: string) {
    console.log('selecting gene ' + gene);
    this.selectedGene = gene;
    this.geneOccurrencePlot = this.projectGeneData.observed_gene_plots[this.selectedLocus][this.selectedGene];
    this.alleleOccurrencePlot = this.projectGeneData.allele_occurrence_plots[this.selectedLocus][this.selectedGene];
    this.alleleZygosityPlot = this.projectGeneData.zygosity_plots[this.selectedLocus][this.selectedGene];
  }
}
